import type { VesselNominationScanResponse } from "@features/vessel-nomination/vessel-scan/VesselNominationScanResults.types";
import { mapVesselNominationsResponse } from "@utils/mappers/vesselNominationResponseMapper";
import { api } from "./api";
import type {
  VesselNominationCompany,
  VesselNominationDetailsResponse,
  VesselNominationHeaderResponse,
  VesselNominationRequestPayload,
  VesselNominationsApiResponse,
  VesselNominationSearchRequest,
  VesselNominationsResponse,
  VesselPreNominationRequestPayload,
} from "./vessel-nomination/types";

const BFF_VESSEL = "bff-vessel";
const SEARCH_VESSEL_NOMINATIONS_PATH = "vessels/vessel-nominations/search";
const COMPANIES_PATH = "companies";

export const QUERY_KEY = "vesselNomination";

export const searchVesselNominations = async ({
  page,
  pageSize,
  body,
}: {
  page: number;
  pageSize: number;
  body: VesselNominationSearchRequest;
}): Promise<VesselNominationsResponse> => {
  const response = await api.post<VesselNominationsApiResponse>(
    BFF_VESSEL,
    `${SEARCH_VESSEL_NOMINATIONS_PATH}?pageNumber=${page - 1}&pageSize=${pageSize}`,
    body,
  );

  return mapVesselNominationsResponse(response);
};

export const getCompanies = async () => {
  return api.get<VesselNominationCompany[]>(BFF_VESSEL, COMPANIES_PATH);
};

export const scanNomination = async ({
  imoOrName,
  nominationType,
  pageNumber,
  pageSize,
}: {
  imoOrName: string;
  nominationType: string;
  pageNumber: number;
  pageSize: number;
}) => {
  return api.get<VesselNominationScanResponse>(BFF_VESSEL, "vessels-nomination:scan", {
    params: {
      imoOrName,
      nominationType,
      pageNumber,
      pageSize,
    },
  });
};

export const checkVesselDeficiencies = async (vesselId: string) =>
  api.get(BFF_VESSEL, "vessels-nomination:deficiency", {
    params: { id: vesselId },
  });

export const postNomination = async <
  T extends VesselNominationRequestPayload | VesselPreNominationRequestPayload,
>({
  nominationPayload,
}: {
  nominationPayload: T;
}) => {
  return api.post<T>(BFF_VESSEL, "vessels/vessel-nominations", nominationPayload);
};

export const putNomination = async <
  T extends VesselNominationRequestPayload | VesselPreNominationRequestPayload,
>({
  nominationPayload,
  nominationId,
}: {
  nominationPayload: T;
  nominationId: string;
}) => {
  return api.put<T>(
    BFF_VESSEL,
    `/vessels/vessel-nomination-resubmit/${nominationId}`,
    nominationPayload,
  );
};

export const getNominationById = async (id: string) => {
  return api.get<VesselNominationDetailsResponse>(BFF_VESSEL, `vessels/vessel-nominations/${id}`);
};

export const getVesselNominationHeader = async (vesselId: string) => {
  return api.get<VesselNominationHeaderResponse>(
    BFF_VESSEL,
    `vessels/vessel-nominations/${vesselId}/vessel-header`,
  );
};

export const updateNomination = async ({
  id,
  nominationPayload,
}: {
  id: string;
  nominationPayload: VesselNominationRequestPayload;
}) => {
  return api.put<VesselNominationRequestPayload>(
    BFF_VESSEL,
    `/vessels/vessel-nominations/${id}`,
    nominationPayload,
  );
};

export const deleteNomination = async ({ id }: { id: string }) => {
  return api.delete(BFF_VESSEL, `vessels/vessel-nominations/${id}`);
};
